import { GridApi } from '@ag-grid-enterprise/all-modules';
import { getDisplayZone } from 'common/types/GetDisplayZone';
import { blankKeyText } from '_legacy/constants/gridText';
import { ZoneMappingConfig, ZoneMappingEntry } from 'store/feature/userSlice';

export const ZONE_FILTER_COLUMNS = [
    'loadZone',
    'dischargeZone',
    'deliveryZone',
] as const;

/**
 * Before saving filters to layout: scan all grid rows to collect the distinct original zone
 * values whose display name (via getDisplayZone) matches the selected filter values.
 * This handles many-to-one mappings correctly — multiple original keys can share one display
 * name, and a simple reverse-lookup through zoneMapping would lose all but the first.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function unmapZoneFilterValues(filterModel: any, api: GridApi): any {
    if (!filterModel) return filterModel;
    const result = { ...filterModel };
    for (const colId of ZONE_FILTER_COLUMNS) {
        const colFilter = result[colId];
        if (!colFilter?.values) continue;

        const selectedDisplayValues = new Set<string>(colFilter.values);
        const matchingOriginals = new Set<string>();

        if (selectedDisplayValues.has(blankKeyText)) {
            matchingOriginals.add(blankKeyText);
            selectedDisplayValues.delete(blankKeyText);
        }

        api.forEachNode((node) => {
            if (node.group || !node.data) return;
            const rawValue: string = node.data[colId];
            if (
                rawValue != null &&
                selectedDisplayValues.has(getDisplayZone(rawValue, colId))
            ) {
                matchingOriginals.add(rawValue);
            }
        });

        result[colId] = { ...colFilter, values: Array.from(matchingOriginals) };
    }
    return result;
}

/**
 * Before applying filters from layout to grid: convert original keys → mapped display names
 * so that AG Grid Set Filter receives the values it expects (as produced by ZoneKeyCreator).
 * Output is deduplicated in case multiple original keys share the same display name.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapZoneFilterValues(
    filterModel: any,
    zoneMappingConfig?: ZoneMappingConfig
): any {
    if (!zoneMappingConfig?.mappings?.length || !filterModel)
        return filterModel;
    const result = { ...filterModel };
    for (const colId of ZONE_FILTER_COLUMNS) {
        const colFilter = result[colId];
        if (colFilter?.values) {
            const mappings: ZoneMappingEntry[] =
                colId === 'dischargeZone'
                    ? zoneMappingConfig.dischMappings?.length
                        ? zoneMappingConfig.dischMappings
                        : zoneMappingConfig.mappings
                    : zoneMappingConfig.mappings;

            result[colId] = {
                ...colFilter,
                values: Array.from(
                    new Set(
                        colFilter.values.map((v: string) => {
                            const entry = mappings.find((e) => e.key === v);
                            return entry ? entry.value : v;
                        })
                    )
                ),
            };
        }
    }
    return result;
}
